/* The hero's Wayfinder.
 *
 * A port of Find My Farmer's homepage search (components/shell/StartMenuSearch.tsx). That file is
 * React against a Postgres RPC; this is a Shopify theme against /search/suggest.json. The code
 * could not come across. The contract did, and it is the contract that makes the thing work:
 *
 *   · TIERED SECTIONS, not one ranked list. A product match must never bury a page, because they
 *     answer different questions. Each tier is a different KIND of destination.
 *   · EVERY ROW SAYS WHERE IT LEADS. A chip on each row names the destination before it is
 *     chosen. Nobody should have to click to find out what a click does.
 *   · ENTER NEVER ROUTES AWAY. Enter searches the showroom — the thing the field promises. Rows
 *     that navigate are chosen deliberately, by click or by arrow key. FMF learned this the hard
 *     way: a search box that teleports you somewhere unasked is a search box people stop using.
 *   · IT WORKS WITH NO SCRIPT. The markup is a plain GET form to the showroom. If this file never
 *     loads, typing and pressing Enter still lands you on a filtered showroom.
 *
 * What is NOT here yet, deliberately: the tier that answers "what story pages are about
 * strawberry". suggest.json matches titles and body text, not themes, so a place or story whose
 * SUBJECT is strawberry without the word in it cannot be found this way. That needs a generated
 * index of the page set — FMF's searchResolve — and the pages have to exist before there is
 * anything to index. Stage two.
 */
(() => {
/* MOUNTED PER FORM, so the hero and the header share ONE implementation rather than two that drift.
   Everything below is scoped to the `form` passed in — there is no shared state between mounts, so
   two Wayfinders on a page cannot fight over the panel, the highlight or the in-flight request.
   The wrapper deliberately sits at zero indent so the body keeps the indentation it was written
   with; re-indenting 240 lines would bury the change in whitespace. */
const mount = (form) => {
  if (!form) return;

  const input = form.querySelector('input[name="q"]');
  const panel = form.querySelector('[data-hero-panel]');
  const action = form.getAttribute('action') || '/collections/showroom';

  /* The panel is moved to <body>, and this is the whole reason the dropdown appeared to do nothing
     the first time it shipped. position:fixed is measured against the viewport ONLY while no
     ancestor is transformed; the hero's .eh__cta is `transform: translateX(-50%)`, and a
     transformed ancestor makes a fixed child behave like an absolute one inside IT. So the panel
     was being placed with viewport coordinates into a box that did not use them — off to the side,
     and clipped away besides by the hero's own overflow:hidden. Out here it is nobody's child. */
  document.body.appendChild(panel);

  /* Two characters before anything is fetched, and a quarter second after the last keystroke.
     Both are FMF's. One character matches most of the catalogue and answers nothing; firing per
     keystroke spends a request on every letter of a word nobody has finished typing. */
  const MIN = 2;
  const WAIT = 250;
  const LIMIT = 6;

  let timer = null;
  let inflight = null;
  let rows = [];        /* every row in the panel, in visual order — the arrow keys walk this */
  let at = -1;          /* which row is highlighted; -1 is "none, Enter searches" */

  const esc = (s) => String(s == null ? '' : s)
    .replace(/[&<>"]/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' }[c]));
  const showroomURL = (q) => action + '?q=' + encodeURIComponent(q);

  /* Shopify hangs its own analytics parameters off every suggestion URL. They are harmless, but
     they are also noise in a shared link, and the product page does not need them. */
  const clean = (u) => String(u || '').split('?')[0];

  function close() {
    panel.hidden = true;
    panel.innerHTML = '';
    rows = [];
    at = -1;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  }

  function highlight(next) {
    if (!rows.length) return;
    if (at > -1) rows[at].classList.remove('is-on');
    at = (next + rows.length) % rows.length;
    const el = rows[at];
    el.classList.add('is-on');
    el.scrollIntoView({ block: 'nearest' });
    input.setAttribute('aria-activedescendant', el.id);
  }

  /* The panel is fixed, so it has to be told where the field is. The hero is overflow:hidden and
     the field sits near the foot of it — an absolutely positioned panel would be clipped away on
     the first result. Below the field by preference, above it when the window's bottom edge is
     nearer than the panel is tall. */
  /* Below the Showroom's phone edge the panel is not a dropdown at all — see openSheet() below. */
  const phone = () => window.innerWidth < 810;

  function place() {
    if (panel.hidden || panel.closest('.sr-sheet')) return;
    const r = form.getBoundingClientRect();
    const gap = 10;
    /* The panel is allowed to be WIDER than the field. The field lost a third of its width to make
       room for the door beside it; the results did not get simpler, and a row still carries a
       thumbnail, a name, a line of detail and a chip saying where it goes. It stays left-aligned
       with the field unless that would push it off-screen, in which case it slides back in. */
    /* On a phone the panel takes the width of the screen, less a gutter: a row carries a thumbnail,
       a name, a line of detail and a chip, and the field it hangs from is already the full row. */
    const want = window.innerWidth < 810
      ? window.innerWidth - 24
      : Math.min(Math.max(r.width, 480), window.innerWidth - 24);
    panel.style.width = want + 'px';
    panel.style.left = Math.max(12, Math.min(r.left, window.innerWidth - want - 12)) + 'px';
    panel.style.maxHeight = '';
    const below = window.innerHeight - r.bottom - gap - 8;
    const above = r.top - gap - 8;
    if (below < 220 && above > below) {
      panel.style.top = 'auto';
      panel.style.bottom = (window.innerHeight - r.top + gap) + 'px';
      panel.style.maxHeight = Math.max(160, above) + 'px';
    } else {
      panel.style.bottom = 'auto';
      panel.style.top = (r.bottom + gap) + 'px';
      panel.style.maxHeight = Math.max(160, below) + 'px';
    }
  }
  window.addEventListener('resize', place);
  window.addEventListener('scroll', place, { passive: true });

  function section(title, items) {
    if (!items.length) return '';
    return `<div class="hw__sec"><p class="hw__sech">${esc(title)}</p>${items.join('')}</div>`;
  }

  /* One row. `chip` is the promise: what happens if you choose this. */
  function row(href, art, title, sub, chip) {
    return `<a class="hw__row" href="${esc(href)}" role="option" aria-selected="false">
      ${art}
      <span class="hw__t"><b>${title}</b>${sub ? `<span>${esc(sub)}</span>` : ''}</span>
      <span class="hw__chip">${esc(chip)}</span>
    </a>`;
  }

  /* The typed text, marked inside the row's own title, so it is obvious WHY a row matched. */
  function mark(text, q) {
    const s = String(text || '');
    const i = s.toLowerCase().indexOf(q.toLowerCase());
    if (i < 0) return esc(s);
    return esc(s.slice(0, i)) + '<mark>' + esc(s.slice(i, i + q.length)) + '</mark>' + esc(s.slice(i + q.length));
  }

  function render(q, r) {
    const P = r.products || [], C = r.collections || [], G = r.pages || [], A = r.articles || [];

    /* The chip names what it opens, and the shop sells two things. Saying "Opens the tea" over a
       list of rubs and peppercorns is the kind of small wrongness that makes a reader trust the
       rest of the page less. Shopify's own product type is the tell. */
    const opens = (type) => {
      const t = String(type || '').toLowerCase();
      if (t.includes('tea')) return 'Opens the tea';
      if (t.includes('spice') || t.includes('herb') || t.includes('salt') || t.includes('blend')) return 'Opens the spice';
      return 'Opens the product';
    };
    const products = P.map((p) => row(
      clean(p.url),
      p.image
        ? `<img class="hw__art" src="${esc(p.image)}" alt="" loading="lazy" width="44" height="44">`
        : '<span class="hw__art"></span>',
      mark(p.title, q),
      [p.type, p.available ? '$' + p.price : 'Sold out'].filter(Boolean).join(' · '),
      opens(p.type),
    ));

    const shelves = C.map((c) => row(
      clean(c.url), '<span class="hw__art hw__art--shelf"></span>',
      mark(c.title, q), '', 'Browse this shelf',
    ));

    /* Pages and articles answer the same kind of question — something to read — so they share a
       tier rather than splitting into two tiers of one row each. */
    const reading = G.map((p) => row(
      clean(p.url), '<span class="hw__art hw__art--page"></span>', mark(p.title, q), 'Page', 'Opens the page',
    )).concat(A.map((a) => row(
      clean(a.url), '<span class="hw__art hw__art--page"></span>', mark(a.title, q), 'From the journal', 'Opens the story',
    )));

    let html = section('Teas & spices', products.slice(0, LIMIT))
      + section('Shelves', shelves.slice(0, 3))
      + section('Answers & stories', reading.slice(0, 4));

    if (!html) {
      html = `<p class="hw__none">Nothing by that name — try the Showroom, where you can narrow by
        what it tastes like, what you are cooking, or what you need it to do.</p>`;
    }

    /* Always last, always present: the thing the field said it would do. */
    html += `<div class="hw__sec hw__sec--go">${row(
      showroomURL(q), '<span class="hw__art hw__art--go"></span>',
      'Search the Showroom for “' + esc(q) + '”',
      'Every tea, spice and blend, with the filters',
      'Searches the Showroom',
    )}</div>`;

    panel.innerHTML = html;
    panel.hidden = false;
    place();
    input.setAttribute('aria-expanded', 'true');
    rows = [...panel.querySelectorAll('.hw__row')];
    rows.forEach((el, i) => { el.id = 'hw-row-' + i; });
    at = -1;
  }

  async function look(q) {
    if (inflight) inflight.abort();
    inflight = new AbortController();
    const url = '/search/suggest.json?q=' + encodeURIComponent(q)
      + '&resources[type]=product,collection,page,article'
      + '&resources[limit]=' + LIMIT
      + '&resources[options][unavailable_products]=last';
    try {
      const res = await fetch(url, { signal: inflight.signal });
      const json = await res.json();
      /* The field may have moved on while this was in the air. */
      if (input.value.trim() !== q) return;
      render(q, (json.resources && json.resources.results) || {});
    } catch (e) {
      if (e.name !== 'AbortError') close();
    }
  }

  const clear = form.querySelector('[data-hero-clear]');
  const div = form.querySelector('[data-hero-div]');
  /* Nothing typed, nothing to cancel — so neither the pill nor the rule beside it is there. */
  function paintClear() {
    const has = input.value.length > 0;
    if (clear) clear.hidden = !has;
    if (div) div.hidden = !has;
  }
  if (clear) {
    clear.addEventListener('click', () => {
      input.value = '';
      paintClear();
      close();
      input.focus();
    });
  }

  input.addEventListener('input', () => {
    const q = input.value.trim();
    paintClear();
    clearTimeout(timer);
    if (q.length < MIN) return close();
    /* On a phone nothing floats under the keyboard; the results arrive as a sheet on Enter. */
    if (phone()) return;
    timer = setTimeout(() => look(q), WAIT);
  });
  paintClear();

  input.addEventListener('keydown', (e) => {
    if (e.key === 'Escape') { close(); input.blur(); return; }
    if (e.key === 'ArrowDown') { e.preventDefault(); return highlight(at + 1); }
    if (e.key === 'ArrowUp') { e.preventDefault(); return highlight(at - 1); }
    if (e.key === 'Enter') {
      /* A highlighted row is a deliberate choice and goes where it says. Nothing highlighted means
         Enter does what the field promises: search the showroom. It never picks a destination on
         the reader's behalf. */
      if (at > -1 && rows[at]) { e.preventDefault(); window.location.assign(rows[at].href); }
    }
  });

  /* Typing again after choosing nothing should bring the panel back. */
  input.addEventListener('focus', () => {
    const q = input.value.trim();
    if (q.length >= MIN && panel.hidden) look(q);
  });

  /* mousedown, not click: a click on a row fires after blur, and blur would have closed the panel
     out from under the pointer. */
  document.addEventListener('mousedown', (e) => {
    /* The panel lives outside the form now, so both have to count as "inside". In a sheet the
       sheet owns dismissal. */
    if (panel.closest('.sr-sheet')) return;
    if (!form.contains(e.target) && !panel.contains(e.target)) close();
  });

  /* ---- On a phone, Enter opens the results as a bottom sheet ----
     Dayton, 2026-09-20: "a user types in their search term and when they press enter it opens a
     mobile bottom sheet with the results. This is the cleanest." The SAME panel — the tiered rows,
     the chips, the Search-the-Showroom row last — moved into the Showroom's sheet primitive
     (assets/showroom-sheet.js, loaded by the layout) and moved back out when it closes. Without
     the primitive the form submits as it always did and the Showroom answers. */
  let sheet = null;
  function openSheet(q) {
    if (!sheet) {
      sheet = window.SRSheet.create({
        ariaLabel: 'Search results',
        onClose: () => { close(); document.body.appendChild(panel); },
      });
    }
    sheet.setContent('<div class="sr-sheet__head"><h2>' + esc(q) + '</h2></div><div class="sr-sheet__scroll hw-sheet"></div>');
    sheet.body.querySelector('.sr-sheet__scroll').appendChild(panel);
    panel.innerHTML = '<p class="hw__none">Looking…</p>';
    panel.hidden = false;
    input.blur();
    sheet.open();
    look(q);
  }
  form.addEventListener('submit', (e) => {
    if (!phone() || !window.SRSheet) return;
    const q = input.value.trim();
    if (q.length < MIN) return;
    e.preventDefault();
    openSheet(q);
  });
};

document.querySelectorAll('[data-hero-search]').forEach(mount);
})();
